/**
 * Display vocabulary for the operator tenant and pledge pages: Tenants,
 * Tenant Detail, Notification Routing, Pledges, Pledge Detail and FI Tasks.
 *
 * Pledges, Pledge Detail and FI Tasks each carried a local StatusBadge that
 * printed the raw API code ("ACTIVE", "RED", "PUBLIC_SECURITIES",
 * "TYPED_NAME") and colored it with bg-info, text-info and bg-neutral, which
 * the tailwind preset does not define, so ACKNOWLEDGED rendered uncolored.
 * The CHA tier, the one alert-grade value on those pages, printed as plain
 * gray text. They now read one set of label maps and compose the Badge
 * primitive from @aaimplatform/ui.
 *
 * Every map is keyed by a GENERATED enum type, so a member added to a spec
 * fails tsc here until it has a label. Several wire fields that carry these
 * values are typed as free strings in platform-admin.openapi.yaml
 * (PledgeSummary.currentChaTier, PledgeSelectedAccountDetail.accountType and
 * .assetClass, PledgeConsentProof.consentMethod), so a value outside the enum
 * can still arrive. It prints "Unrecognized" with the received code in the
 * title attribute, never the raw code on screen and never a guessed label.
 *
 * Asset-class labels are NOT here: the terminology register
 * (.claude/manifests/terminology/terms/collateral.yaml, term asset-class,
 * context operatorConfig) generates them into
 * src/generated/terminology-labels.ts as ASSET_CLASS_LABELS, and that module is
 * the one import site for them in this app.
 *
 * The admin-portal holds the rest of this vocabulary in
 * apps/admin-portal/src/pages/pledges/pledge-display.tsx. Two apps needing one
 * vocabulary is the case .claude/rules/ui-package.md Rule 1 sends to
 * @aaimplatform/ui; until the kit exports it, this file is the single
 * platform-admin copy, and its labels match the admin-portal's except
 * AWAITING_SIGNATURE (see PLEDGE_STATUS_LABEL).
 */

import { Badge } from '@aaimplatform/ui';
import type { components as PlatformAdminComponents } from '@aaimplatform/institutional-types/platform-admin';
import type { components as CollateralPolicyComponents } from '@aaimplatform/institutional-types/collateral-policy';
import type { components as FinancialProviderComponents } from '@aaimplatform/institutional-types/financial-provider';

type PledgeStatus = PlatformAdminComponents['schemas']['PledgeStatus'];
type TenantStatus = PlatformAdminComponents['schemas']['TenantStatus'];
/**
 * Why the tenant exists (platform-admin.openapi.yaml TenantPurpose; operator
 * ruling 2026-10-22, set once at provisioning).
 */
type TenantPurpose = PlatformAdminComponents['schemas']['TenantPurpose'];
/**
 * The four-tier CHA ladder, RED terminal (collateral-policy.openapi.yaml
 * CollateralHealthTier).
 */
type CollateralHealthTier = CollateralPolicyComponents['schemas']['CollateralHealthTier'];
type AccountType = FinancialProviderComponents['schemas']['AccountType'];
type AccountSubtype = FinancialProviderComponents['schemas']['AccountSubtype'];
type ConsentMethod =
  FinancialProviderComponents['schemas']['ConnectorAuthorizationConsent']['consentMethod'];

/** The Badge primitive variants a status badge on these pages renders in. */
type BadgeVariantState = 'success' | 'warning' | 'error' | 'neutral';

/** What a label lookup prints for a value outside its enum. */
export const UNRECOGNIZED_LABEL = 'Unrecognized';

/** What a label lookup prints for an absent value. */
export const ABSENT_LABEL = '--';

// ---------------------------------------------------------------------------
// Label maps
// ---------------------------------------------------------------------------

export const PLEDGE_STATUS_LABEL: Record<PledgeStatus, string> = {
  PENDING: 'Pending',
  ACKNOWLEDGED: 'Acknowledged',
  ACTIVE: 'Active',
  RELEASED: 'Released',
  EXPIRED: 'Expired',
  WITHDRAWN: 'Withdrawn',
  DECLINED: 'Declined',
  RETURNED: 'Returned',
  // GH #2300 / #2194 node 9. The operator needs to know WHOSE signature is
  // outstanding. fi-person-noun-and-signature-label (operator ruling): both
  // portals read "Awaiting customer signature", the admin-portal map in
  // pages/pledges/pledge-display.tsx included. Registered as term
  // pledge-status-awaiting-signature in terminology/terms/collateral.yaml.
  AWAITING_SIGNATURE: 'Awaiting customer signature',
};

/**
 * Status filter options for every page that filters pledges by status
 * (Pledges and FI Tasks), derived from {@link PLEDGE_STATUS_LABEL}. The record
 * is exhaustive over the generated PledgeStatus union, so a status added to
 * schema-registry/platform-admin.openapi.yaml fails tsc here until it has a
 * label, and then appears in every filter at once. Hand-written lists drifted
 * twice: Pledges omitted DECLINED and RETURNED, and FI Tasks omitted
 * AWAITING_SIGNATURE.
 */
export const PLEDGE_STATUS_FILTER_OPTIONS: ReadonlyArray<{ value: PledgeStatus; label: string }> =
  (Object.keys(PLEDGE_STATUS_LABEL) as PledgeStatus[]).map((value) => ({
    value,
    label: PLEDGE_STATUS_LABEL[value],
  }));

export const TENANT_STATUS_LABEL: Record<TenantStatus, string> = {
  PENDING: 'Pending',
  ACTIVE: 'Active',
  SUSPENDED: 'Suspended',
  INACTIVE: 'Inactive',
};

export const TENANT_PURPOSE_LABEL: Record<TenantPurpose, string> = {
  CUSTOMER: 'Customer',
  PILOT: 'Pilot',
  DEMO: 'Demo',
  PROBE: 'Probe',
};

/**
 * Purpose options for the Onboard New Tenant form, in the spec's order and
 * derived from {@link TENANT_PURPOSE_LABEL}, so a purpose added to the spec
 * fails tsc there until it has a label and then appears here.
 */
export const TENANT_PURPOSE_OPTIONS: ReadonlyArray<{ value: TenantPurpose; label: string }> =
  (Object.keys(TENANT_PURPOSE_LABEL) as TenantPurpose[]).map((value) => ({
    value,
    label: TENANT_PURPOSE_LABEL[value],
  }));

export const CHA_TIER_LABEL: Record<CollateralHealthTier, string> = {
  GREEN: 'Green',
  YELLOW: 'Yellow',
  ORANGE: 'Orange',
  RED: 'Red',
};

/** A pledge whose projection carries no tier has not been assessed. */
export const CHA_TIER_UNCLASSIFIED_LABEL = 'Unclassified';

/**
 * AccountType and AccountSubtype from financial-provider.openapi.yaml. The
 * pledge projection's accountType is a free string that carries either
 * vocabulary (the OICU seed pledges a 'brokerage' account, a member of both).
 */
export const ACCOUNT_TYPE_LABEL: Record<AccountType | AccountSubtype, string> = {
  depository: 'Depository',
  credit: 'Credit',
  loan: 'Loan',
  investment: 'Investment',
  brokerage: 'Brokerage',
  other: 'Other',
  checking: 'Checking',
  savings: 'Savings',
  money_market: 'Money market',
  cd: 'Certificate of deposit',
  cash_management: 'Cash management',
  paypal: 'PayPal',
  prepaid: 'Prepaid',
  credit_card: 'Credit card',
  line_of_credit: 'Line of credit',
  auto: 'Auto loan',
  mortgage: 'Mortgage',
  student: 'Student loan',
  personal: 'Personal loan',
  home_equity: 'Home equity',
  retirement: 'Retirement',
  '401k': '401(k)',
  '403b': '403(b)',
  ira: 'IRA',
  roth_ira: 'Roth IRA',
  hsa: 'HSA',
  mutual_fund: 'Mutual fund',
  trust: 'Trust',
};

export const CONSENT_METHOD_LABEL: Record<ConsentMethod, string> = {
  TYPED_NAME: 'Typed name',
  CHECKBOX_ACKNOWLEDGE: 'Checkbox acknowledgment',
};

// ---------------------------------------------------------------------------
// Lookup
// ---------------------------------------------------------------------------

function hasKey<K extends string>(map: Record<K, string>, value: string): value is K {
  return Object.prototype.hasOwnProperty.call(map, value);
}

/**
 * Label for a value drawn from a generated enum. An absent value prints
 * {@link ABSENT_LABEL}; a value outside the enum prints
 * {@link UNRECOGNIZED_LABEL}.
 */
export function enumLabel<K extends string>(
  map: Record<K, string>,
  value: string | null | undefined,
): string {
  if (value === null || value === undefined || value === '') return ABSENT_LABEL;
  return hasKey(map, value) ? map[value] : UNRECOGNIZED_LABEL;
}

/**
 * {@link enumLabel} as an element. An unrecognized value keeps the received
 * code in the title attribute so support can still read it.
 */
export function EnumText<K extends string>({
  map,
  value,
  className,
}: {
  map: Record<K, string>;
  value: string | null | undefined;
  className?: string;
}) {
  const label = enumLabel(map, value);
  const unrecognized = label === UNRECOGNIZED_LABEL;
  return (
    <span className={className} title={unrecognized && value ? value : undefined}>
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Badges
// ---------------------------------------------------------------------------

function LabeledBadge({
  label,
  code,
  variant,
}: {
  label: string;
  code: string | null | undefined;
  variant: BadgeVariantState;
}) {
  return (
    <span
      className="inline-flex"
      title={label === UNRECOGNIZED_LABEL && code ? code : undefined}
    >
      <Badge variant={variant} size="medium">
        {label}
      </Badge>
    </span>
  );
}

function pledgeStatusVariant(status: string): BadgeVariantState {
  switch (status) {
    case 'PENDING':
    case 'RETURNED':
    case 'AWAITING_SIGNATURE':
      return 'warning';
    case 'ACTIVE':
      return 'success';
    case 'DECLINED':
    case 'WITHDRAWN':
      return 'error';
    default:
      return 'neutral';
  }
}

export function PledgeStatusBadge({ status }: { status: string }) {
  return (
    <LabeledBadge
      label={enumLabel(PLEDGE_STATUS_LABEL, status)}
      code={status}
      variant={pledgeStatusVariant(status)}
    />
  );
}

function tenantStatusVariant(status: string): BadgeVariantState {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'PENDING':
      return 'warning';
    case 'SUSPENDED':
      return 'error';
    default:
      return 'neutral';
  }
}

export function TenantStatusBadge({ status }: { status: string }) {
  return (
    <LabeledBadge
      label={enumLabel(TENANT_STATUS_LABEL, status)}
      code={status}
      variant={tenantStatusVariant(status)}
    />
  );
}

/**
 * DEMO and PROBE rows hold no contracted institution's data, so they carry the
 * warning tone in a list an operator reads as the book; CUSTOMER and PILOT
 * are real institutions and stay neutral. Red stays reserved for alerts.
 */
function tenantPurposeVariant(purpose: string): BadgeVariantState {
  switch (purpose) {
    case 'DEMO':
    case 'PROBE':
      return 'warning';
    default:
      return 'neutral';
  }
}

/** A tenant's purpose as a badge, beside its status on Tenants and Tenant Detail. */
export function TenantPurposeBadge({ purpose }: { purpose: string }) {
  return (
    <LabeledBadge
      label={enumLabel(TENANT_PURPOSE_LABEL, purpose)}
      code={purpose}
      variant={tenantPurposeVariant(purpose)}
    />
  );
}

function chaTierVariant(tier: string | null | undefined): BadgeVariantState {
  switch (tier) {
    case 'GREEN':
      return 'success';
    case 'YELLOW':
    case 'ORANGE':
      return 'warning';
    case 'RED':
      return 'error';
    default:
      return 'neutral';
  }
}

/**
 * The CHA tier as a badge. No tier prints "Unclassified"; a value outside the
 * four-tier ladder prints "Unrecognized" with the code in its title.
 */
export function ChaTierBadge({ tier }: { tier: string | null | undefined }) {
  const label = tier ? enumLabel(CHA_TIER_LABEL, tier) : CHA_TIER_UNCLASSIFIED_LABEL;
  return <LabeledBadge label={label} code={tier} variant={chaTierVariant(tier)} />;
}

/**
 * A tenant's Pledged-Asset Lending entitlement as a badge. Tenants and Tenant
 * Detail drew this chip from raw classes, so on Tenant Detail it sat beside
 * the status badge in a different shape. `prefixed` reads "PAL Enabled" where
 * no column header names the field.
 */
export function PalStateBadge({
  enabled,
  prefixed = false,
}: {
  enabled: boolean;
  prefixed?: boolean;
}) {
  const state = enabled ? 'Enabled' : 'Disabled';
  return (
    <LabeledBadge
      label={prefixed ? `PAL ${state}` : state}
      code={null}
      variant={enabled ? 'success' : 'neutral'}
    />
  );
}

/** A pledged account's or holding's eligibility, under an "Eligible" header. */
export function EligibilityBadge({ eligible }: { eligible: boolean }) {
  return (
    <LabeledBadge label={eligible ? 'Yes' : 'No'} code={null} variant={eligible ? 'success' : 'error'} />
  );
}
